/**
 * Tracker domain contracts (APP-2, ADR 0052).
 *
 * WHY Zod here: the tracker persists whole workspace snapshots (localStorage today,
 * Supabase rows tomorrow) and re-reads them at the boundary — persisted-on-read is
 * external input, so it is PARSED (ADR 0005) against these schemas by both the web
 * app and the tracker api. Like `http/`, this is a cross-service wire shape.
 *
 * The model is deliberately opinionated (the Linear lesson — constraints are the
 * product): fixed status categories, fixed 5-level priority, a single assignee,
 * one points estimate scale. Custom-per-team is limited to state names/order.
 */
import { z } from 'zod';

/** Epoch milliseconds (injected clock; never ad-hoc Date.now in services). */
const epochMs = z.number().int().nonnegative();

/** Fixed workflow categories, in canonical board order. Not user-extensible. */
export const STATUS_CATEGORIES = ['triage', 'backlog', 'unstarted', 'started', 'completed', 'canceled'] as const;
export const StatusCategorySchema = z.enum(STATUS_CATEGORIES);
export type StatusCategory = z.infer<typeof StatusCategorySchema>;

/** Fixed priority scale: 0 none · 1 urgent · 2 high · 3 medium · 4 low (Linear's ordering). */
export const PRIORITIES = [0, 1, 2, 3, 4] as const;
export const PrioritySchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
export type Priority = z.infer<typeof PrioritySchema>;
export const PRIORITY_LABELS: Readonly<Record<Priority, string>> = {
  0: 'No priority',
  1: 'Urgent',
  2: 'High',
  3: 'Medium',
  4: 'Low',
};

export const TrackerUserSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** Two-letter avatar monogram. */
  initials: z.string().min(1).max(3),
  /** Avatar accent color (CSS color string, e.g. an oklch() literal). */
  color: z.string().min(1),
});
export type TrackerUser = z.infer<typeof TrackerUserSchema>;

export const WorkflowStateSchema = z.object({
  id: z.string().min(1),
  teamId: z.string().min(1),
  name: z.string().min(1),
  category: StatusCategorySchema,
  /** Order within its category (categories themselves are fixed). */
  position: z.number().int().nonnegative(),
  /** State dot color. */
  color: z.string().min(1),
});
export type WorkflowState = z.infer<typeof WorkflowStateSchema>;

export const TrackerTeamSchema = z.object({
  id: z.string().min(1),
  /** Short uppercase key — the `TRK` in `TRK-12`. */
  key: z.string().regex(/^[A-Z][A-Z0-9]{0,6}$/),
  name: z.string().min(1),
  /**
   * Parent team id for sub-team hierarchy (Linear-synced teams can nest).
   * Optional + nullable so pre-hierarchy snapshots parse unchanged; `null`/absent
   * = a top-level team. Additive — no `WorkspaceSnapshot.version` bump (ADR 0070).
   */
  parentTeamId: z.string().min(1).nullable().optional(),
  /** New issues land here (must be a `backlog`-category state of this team). */
  defaultStateId: z.string().min(1),
  /** Whether the triage inbox is enabled for this team. */
  triageEnabled: z.boolean(),
  /** Highest issue number ever allocated (identifiers are never reused). */
  issueCounter: z.number().int().nonnegative(),
});
export type TrackerTeam = z.infer<typeof TrackerTeamSchema>;

export const LabelSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  color: z.string().min(1),
});
export type Label = z.infer<typeof LabelSchema>;

export const CycleSchema = z.object({
  id: z.string().min(1),
  teamId: z.string().min(1),
  /** 1-based, per team. */
  number: z.number().int().positive(),
  startsAt: epochMs,
  /** Exclusive end — the next cycle starts here. */
  endsAt: epochMs,
});
export type Cycle = z.infer<typeof CycleSchema>;

export const PROJECT_STATUSES = ['backlog', 'planned', 'started', 'paused', 'completed', 'canceled'] as const;
export const ProjectStatusSchema = z.enum(PROJECT_STATUSES);
export type ProjectStatus = z.infer<typeof ProjectStatusSchema>;

export const TrackerProjectSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  status: ProjectStatusSchema,
  leadId: z.string().min(1).nullable(),
  /** Target date as epoch ms (fuzzy granularity is a V2 concern). */
  targetDate: epochMs.nullable(),
  createdAt: epochMs,
});
export type TrackerProject = z.infer<typeof TrackerProjectSchema>;

export const TrackerIssueSchema = z.object({
  id: z.string().min(1),
  teamId: z.string().min(1),
  /** Per-team sequential number; identifier = `${team.key}-${number}`. */
  number: z.number().int().positive(),
  title: z.string().min(1),
  /** Markdown source (rendered read-only in MVP). */
  description: z.string(),
  stateId: z.string().min(1),
  priority: PrioritySchema,
  assigneeId: z.string().min(1).nullable(),
  labelIds: z.array(z.string().min(1)),
  /** Points; null = unestimated. */
  estimate: z.number().int().positive().nullable(),
  dueDate: epochMs.nullable(),
  cycleId: z.string().min(1).nullable(),
  projectId: z.string().min(1).nullable(),
  creatorId: z.string().min(1),
  subscriberIds: z.array(z.string().min(1)),
  /** Manual ordering within a group (lower sorts first). */
  sortOrder: z.number(),
  /** Snoozed-until for triage issues (hidden from triage while in the future). */
  snoozedUntil: epochMs.nullable(),
  createdAt: epochMs,
  updatedAt: epochMs,
  completedAt: epochMs.nullable(),
  canceledAt: epochMs.nullable(),
});
export type TrackerIssue = z.infer<typeof TrackerIssueSchema>;

/** Identifier helper — the one way an issue renders its `TRK-12` code. */
export function issueIdentifier(team: Pick<TrackerTeam, 'key'>, issue: Pick<TrackerIssue, 'number'>): string {
  return `${team.key}-${issue.number}`;
}

export const CommentSchema = z.object({
  id: z.string().min(1),
  issueId: z.string().min(1),
  authorId: z.string().min(1),
  body: z.string().min(1),
  createdAt: epochMs,
});
export type IssueComment = z.infer<typeof CommentSchema>;

export const ACTIVITY_KINDS = [
  'created',
  'state',
  'priority',
  'assignee',
  'labels',
  'estimate',
  'due-date',
  'cycle',
  'project',
  'title',
  'triage-accepted',
  'triage-declined',
  'triage-snoozed',
  'comment',
] as const;
export const ActivityKindSchema = z.enum(ACTIVITY_KINDS);
export type ActivityKind = z.infer<typeof ActivityKindSchema>;

export const ActivityEntrySchema = z.object({
  id: z.string().min(1),
  issueId: z.string().min(1),
  actorId: z.string().min(1),
  kind: ActivityKindSchema,
  /** Human-readable before/after, already resolved to display strings. */
  from: z.string().nullable(),
  to: z.string().nullable(),
  occurredAt: epochMs,
});
export type ActivityEntry = z.infer<typeof ActivityEntrySchema>;

export const NOTIFICATION_KINDS = ['assigned', 'comment', 'status', 'triage'] as const;
export const NotificationSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(NOTIFICATION_KINDS),
  issueId: z.string().min(1),
  actorId: z.string().min(1),
  /** Pre-rendered, non-sensitive display line. */
  message: z.string().min(1),
  read: z.boolean(),
  createdAt: epochMs,
});
export type TrackerNotification = z.infer<typeof NotificationSchema>;

/**
 * The whole-workspace snapshot — the unit the repository port loads, and the shape
 * persisted-on-read is parsed against. Small by design (MVP scale); pagination and
 * delta sync are explicitly V2 (see the tracker backlog).
 */
/* ── Linear sync (APP-2, ADR 0070/0071) ───────────────────────────────────── */

/** Sync direction: off (dormant) · pull (Linear→tracker) · two_way (pull + push). */
export const SYNC_DIRECTIONS = ['off', 'pull', 'two_way'] as const;
export const SyncDirectionSchema = z.enum(SYNC_DIRECTIONS);
export type SyncDirection = z.infer<typeof SyncDirectionSchema>;

/** The mapped entity kinds tracked in the Linear↔tracker link table. */
export const SYNC_LINK_KINDS = ['team', 'state', 'label', 'user', 'issue'] as const;
export const SyncLinkKindSchema = z.enum(SYNC_LINK_KINDS);
export type SyncLinkKind = z.infer<typeof SyncLinkKindSchema>;

/** Per-link sync state (surfaced as a per-issue badge). */
export const SYNC_STATES = ['synced', 'pending', 'conflict', 'error'] as const;
export const SyncStateSchema = z.enum(SYNC_STATES);
export type SyncState = z.infer<typeof SyncStateSchema>;

export const SyncRunStatusSchema = z.enum(['pending', 'success', 'partial', 'error']);
export type SyncRunStatus = z.infer<typeof SyncRunStatusSchema>;

/** Counts a reconcile reports (pull/push × created/updated/skipped/conflicts). */
export const SyncRunCountsSchema = z.record(z.number().int().nonnegative());
export type SyncRunCounts = z.infer<typeof SyncRunCountsSchema>;

export const SyncRunSummarySchema = z.object({
  status: SyncRunStatusSchema,
  direction: SyncDirectionSchema,
  counts: SyncRunCountsSchema,
  error: z.string().nullable(),
  startedAt: epochMs,
  finishedAt: epochMs.nullable(),
});
export type SyncRunSummary = z.infer<typeof SyncRunSummarySchema>;

/** GET /sync/status payload — drives the UI sync affordance. */
export const SyncStatusSchema = z.object({
  /** Capability gate open (LINEAR_SYNC_ENABLED + OAuth client configured). */
  enabled: z.boolean(),
  /** A Linear OAuth token is stored for this owner (the Connect flow completed). */
  connected: z.boolean(),
  direction: SyncDirectionSchema,
  lastRun: SyncRunSummarySchema.nullable(),
});
export type SyncStatus = z.infer<typeof SyncStatusSchema>;

export const WorkspaceSnapshotSchema = z.object({
  /** Snapshot schema version — bump alongside breaking shape changes. */
  version: z.literal(1),
  users: z.array(TrackerUserSchema),
  /** The acting principal's user id (single-user simulation today). */
  meUserId: z.string().min(1),
  teams: z.array(TrackerTeamSchema),
  states: z.array(WorkflowStateSchema),
  labels: z.array(LabelSchema),
  cycles: z.array(CycleSchema),
  projects: z.array(TrackerProjectSchema),
  issues: z.array(TrackerIssueSchema),
  comments: z.array(CommentSchema),
  activity: z.array(ActivityEntrySchema),
  notifications: z.array(NotificationSchema),
});
export type WorkspaceSnapshot = z.infer<typeof WorkspaceSnapshotSchema>;
